import type { EventManager } from '../event-manager.class';

/**
 * Pointer travel (px) past which a press counts as a drag rather than a click.
 * Small enough that a steady click still selects, large enough to absorb the
 * hand tremor of a normal click.
 */
const DRAG_THRESHOLD = 5;

/**
 * Track whether the pointer is being dragged, so a camera move does not read as
 * a click.
 *
 * Orbiting, panning and dollying all end with the pointer released somewhere in
 * the scene, and the browser follows that release with a `click` (and, for the
 * right button, a `contextmenu`) on whatever sits under it. The click handlers
 * consult `internalPointerDragged` and drop those.
 */
export const setupDragTrackingCfn = (self: EventManager, _params: Record<string, never>): void => {
	const { domElement } = self.internalConfig;

	const handlePointerDown = (event: PointerEvent): void => {
		self.internalPointerDownX = event.clientX;
		self.internalPointerDownY = event.clientY;
		self.internalPointerDragged = false;
	};

	const handlePointerMove = (event: PointerEvent): void => {
		// `buttons === 0` means nothing is held — plain hover movement, and also
		// the case where the press was released outside the canvas.
		if (event.buttons === 0 || self.internalPointerDragged) return;

		const dx = event.clientX - self.internalPointerDownX;
		const dy = event.clientY - self.internalPointerDownY;

		// Latched: a gesture that wanders past the threshold stays a drag even if
		// it comes back to where it started, as a full-circle orbit does.
		if (Math.sqrt((dx * dx) + (dy * dy)) > DRAG_THRESHOLD) {
			self.internalPointerDragged = true;
		}
	};

	self.internalHandlePointerDown = handlePointerDown;
	self.internalHandlePointerMove = handlePointerMove;

	domElement.addEventListener('pointerdown', handlePointerDown);
	domElement.addEventListener('pointermove', handlePointerMove);
};
